import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormInput, FormSelect, FormDateInput } from "../components/ui";
import {
  ArrowBack as BackIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as WonIcon,
  Cancel as LostIcon,
  AttachMoney as AmountIcon,
  CalendarToday as CalIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  TrendingUp as DealIcon,
  StickyNote2 as NoteIcon,
  Task as TaskIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Apartment as OrgIcon,
  Category as LobIcon,
  History as HistoryIcon,
  SwapHoriz as SwapIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { observer } from "mobx-react-lite";
import {
  dealsApi,
  Deal,
  DealActivity,
  PipelineStage,
  DEAL_PRIORITY_LABELS,
  DEAL_STATUS_LABELS,
  DEAL_SOURCE_LABELS,
} from "../lib/api/dealsApi";
import { themeColors } from "../app/layout/theme";
import { SideCard } from "../components/shared/SideCard";
import { RequirementsSideCard } from "../components/shared/RequirementsSideCard";
import { AddressAndTaxInfoCard } from "../components/shared/AddressAndTaxInfoCard";
import { DescriptionSection } from "../components/shared/DescriptionSection";
import { InfoRow } from "../components/shared/DetailViewWrappers";
import { EntityModal } from "../components/shared/EntityModal";
import {
  CommentItem,
  CommentInput,
  CommentsEmptyState,
} from "../components/shared/NotesSection";
import { EntityDetailTabs } from "../components/shared/EntityDetailTabs";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { DeleteDialog } from "../components/shared/DeleteDialog";
import { HelpAssigneeSelector } from "../components/shared/HelpAssigneeSelector";
import AttachmentsSection from "../components/shared/AttachmentsSection";
import DealTasksSection from "../components/deals/DealTasksSection";
import { UniversalActivityTimeline, groupActivities, normalizeActivities } from "../components/shared/UniversalActivityTimeline";
import DealCallsSection from "../components/deals/DealCallsSection";
import DealEventsSection from "../components/deals/DealEventsSection";
import {
  SendEmailDrawer,
  SendEmailData,
} from "../components/drawers/SendEmailDrawer";
import { emailApi } from "../lib/api/emailApi";
import { EmailHistorySection } from "../components/shared/EmailHistorySection";
import { callStore } from "../lib/stores/callStore";
import {
  callLogsApi,
  CallLog,
  CallStatus,
  SaveOutcomePayload,
} from "../lib/api/callLogsApi";
import { contactsApi, Contact } from "../lib/api/contactsApi";
import { customersApi } from "../lib/api/customersApi";
import { usersAdminApi, AdminUser } from "../lib/api/usersAdminApi";
import { usePermission } from "../lib/hooks/usePermission";
import { leadsApi } from "../lib/api/leadsApi";
import { salesApi } from "../lib/api/salesApi";
import { dealFollowUpsApi } from "../lib/api/dealFollowUpsApi";
import { authStore } from "../lib/stores/authStore";
import { LocationIcon, IndustryIcon } from "../components/shared/CustomIcons";

// ── Select Email Contact Modal ────────────────────────────────────────────────

function SelectEmailContactModal({
  open,
  customerId,
  onClose,
  onSelect,
}: {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
  onSelect: (emails: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["contacts-for-email", customerId],
    queryFn: () => contactsApi.getAll({ customerId: customerId!, limit: 100 }),
    enabled: open && !!customerId,
  });

  const contacts: Contact[] = data?.items ?? [];
  const withEmail = contacts.filter((c) => c.email);

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) {
      toast.error("Select at least one recipient");
      return;
    }
    onSelect([...selected]);
    setSelected(new Set());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle
        sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        <EmailIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">
          Select Recipients
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : withEmail.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <EmailIcon sx={{ fontSize: 36, color: "#e0e0e0", mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">
              No contacts with email addresses found for this company.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {withEmail.map((c) => {
              const isOn = selected.has(c.email!);
              return (
                <Box
                  key={c.id}
                  onClick={() => toggle(c.email!)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: `1.5px solid ${isOn ? themeColors.primary : "#e5e7eb"}`,
                    bgcolor: isOn ? `${themeColors.primary}08` : "#fafafa",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: themeColors.primary,
                      bgcolor: `${themeColors.primary}06`,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "12px",
                      fontWeight: 700,
                      bgcolor: isOn ? themeColors.primary : "#9ca3af",
                      flexShrink: 0,
                    }}
                  >
                    {c.name
                      .split(" ")
                      .map((w: string) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={600} noWrap>
                      {c.name}
                    </Typography>
                    <Typography fontSize="11.5px" color="text.secondary" noWrap>
                      {c.email}
                    </Typography>
                    {c.role && (
                      <Typography fontSize="10.5px" color="text.disabled">
                        {c.role}
                      </Typography>
                    )}
                  </Box>
                  {isOn && (
                    <CheckIcon
                      sx={{
                        fontSize: 16,
                        color: themeColors.primary,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selected.size === 0 || withEmail.length === 0}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            bgcolor: themeColors.primary,
          }}
        >
          Compose Email
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Select Call Contact Modal ─────────────────────────────────────────────────

function SelectCallContactModal({
  open,
  customerId,
  onClose,
  onSelect,
}: {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
  onSelect: (contact: Contact) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["contacts-for-call", customerId],
    queryFn: () => contactsApi.getAll({ customerId: customerId!, limit: 100 }),
    enabled: open && !!customerId,
  });

  const contacts: Contact[] = data?.items ?? [];
  const withPhone = contacts.filter((c) => c.phone);

  const handleConfirm = () => {
    if (!selectedId) {
      toast.error("Select a contact to call");
      return;
    }
    const contact = withPhone.find(c => c.id === selectedId);
    if (contact) {
      onSelect(contact);
    }
    setSelectedId(null);
  };

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle
        sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        <PhoneIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">
          Select Contact to Call
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : withPhone.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <PhoneIcon sx={{ fontSize: 36, color: "#e0e0e0", mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">
              No contacts with phone numbers found for this company.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {withPhone.map((c) => {
              const isOn = selectedId === c.id;
              return (
                <Box
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: `1.5px solid ${isOn ? themeColors.primary : "#e5e7eb"}`,
                    bgcolor: isOn ? `${themeColors.primary}08` : "#fafafa",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: themeColors.primary,
                      bgcolor: `${themeColors.primary}06`,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "12px",
                      fontWeight: 700,
                      bgcolor: isOn ? themeColors.primary : "#9ca3af",
                      flexShrink: 0,
                    }}
                  >
                    {c.name
                      .split(" ")
                      .map((w: string) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={600} noWrap>
                      {c.name}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <PhoneIcon sx={{ fontSize: 11, color: "#9ca3af" }} />
                      <Typography
                        fontSize="11.5px"
                        color="text.secondary"
                        noWrap
                      >
                        {c.phone}
                      </Typography>
                    </Box>
                    {c.role && (
                      <Typography fontSize="10.5px" color="text.disabled">
                        {c.role}
                      </Typography>
                    )}
                  </Box>
                  {isOn && (
                    <CheckIcon
                      sx={{
                        fontSize: 16,
                        color: themeColors.primary,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedId || withPhone.length === 0}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            bgcolor: themeColors.primary,
          }}
        >
          Start Call
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1970) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
function fmtAmount(amount: number | null, currency: string) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}
function initials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}
const AVATAR_COLORS = [
  "#0070d2",
  "#2e7d32",
  "#7b1fa2",
  "#f57c00",
  "#c62828",
  "#00838f",
];
function avatarColor(name: string) {
  let h = 0;
  for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const PRIORITY_COLORS: Record<string, { bg: string; color: string }> = {
  LOW: { bg: "#f0fdf4", color: "#16a34a" },
  MEDIUM: { bg: "#fefce8", color: "#ca8a04" },
  HIGH: { bg: "#fff7ed", color: "#ea580c" },
  URGENT: { bg: "#fef2f2", color: "#dc2626" },
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  OPEN: { bg: "#eff6ff", color: "#2563eb" },
  WON: { bg: "#f0fdf4", color: "#16a34a" },
  LOST: { bg: "#fef2f2", color: "#dc2626" },
  SALES: { bg: "#f5f3ff", color: "#7c3aed" },
};

// ── Stage Progress Bar ────────────────────────────────────────────────────────

function StageProgressBar({
  deal,
  stages,
  onStageClick,
  isChanging,
}: {
  deal: Deal;
  stages: PipelineStage[];
  onStageClick?: (stageName: string) => void;
  isChanging?: boolean;
}) {
  const activeStages = stages.filter((s) => !s.isWon && !s.isLost);
  const currentIdx = activeStages.findIndex((s) => s.name === deal.stage);
  const stg = stages.find((s) => s.name === deal.stage);
  const isWonOrSales = deal.isWon || deal.status === "SALES";
  const pct = isWonOrSales
    ? 100
    : deal.isLost
      ? 0
      : stg?.probabilityPct ?? (activeStages.length > 1
        ? Math.round((currentIdx / (activeStages.length - 1)) * 90)
        : 50);

  // Only forward movement is allowed; past stages are disabled
  const canClickForward = !!onStageClick && !isWonOrSales && !deal.isLost;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "center",
        }}
      >
        <Typography fontSize="12px" fontWeight={600} color="text.secondary">
          Pipeline Progress
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isChanging && (
            <CircularProgress size={10} sx={{ color: themeColors.primary }} />
          )}
          <Typography
            fontSize="12px"
            fontWeight={700}
            color={themeColors.primary}
          >
            {pct}%
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "#e5e7eb",
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            bgcolor: deal.isWon
              ? "#16a34a"
              : deal.isLost
                ? "#dc2626"
                : themeColors.primary,
            transition: "width 0.4s ease",
          },
        }}
      />
      {canClickForward && (
        <Typography
          fontSize="10px"
          color="text.disabled"
          sx={{ mt: 0.75, mb: 0.25 }}
        >
          Click a stage to move the deal forward
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          mt: canClickForward ? 0.25 : 1.5,
          flexWrap: "wrap",
        }}
      >
        {activeStages.map((s, i) => {
          const isCurrent = i === currentIdx;
          const isPast = i < currentIdx;
          const isFuture = i > currentIdx;
          // Only the immediate next stage is clickable (sequential level-wise progression)
          const isClickable = canClickForward && i === currentIdx + 1;

          return (
            <Tooltip
              key={s.id}
              title={
                isCurrent
                  ? `Current stage: ${s.name}`
                  : isPast
                    ? `Completed: ${s.name}`
                    : isClickable
                      ? `Move to "${s.name}"`
                      : s.name
              }
              arrow
            >
              <Box
                onClick={() => isClickable && onStageClick?.(s.name)}
                sx={{
                  flex: 1,
                  minWidth: 50,
                  textAlign: "center",
                  py: 0.5,
                  px: 0.5,
                  borderRadius: "4px",
                  position: "relative",
                  // ── Current stage: bold highlight ──
                  ...(isCurrent && {
                    cursor: "default",
                    bgcolor: `${s.color}20`,
                    border: `2px solid ${s.color}`,
                    transform: "scaleY(1.1)",
                    boxShadow: `0 2px 10px ${s.color}50`,
                  }),
                  // ── Past stages: completed / locked ──
                  ...(isPast && {
                    cursor: "not-allowed",
                    bgcolor: "#f0fdf4",
                    border: "1.5px solid #86efac",
                    opacity: 0.75,
                  }),
                  // ── Future stages: clickable to advance ──
                  ...(isFuture && {
                    cursor: isClickable ? "pointer" : "default",
                    bgcolor: "#f8fafc",
                    border: "1.5px solid #e2e8f0",
                  }),
                  transition: "all 0.15s",
                  "&:hover": isClickable
                    ? {
                        bgcolor: `${s.color}18`,
                        border: `1.5px solid ${s.color}`,
                        transform: "scaleY(1.05)",
                      }
                    : {},
                }}
              >
                {/* Completed tick for past stages */}
                {isPast && (
                  <CheckIcon
                    sx={{
                      fontSize: 8,
                      color: "#16a34a",
                      position: "absolute",
                      top: 2,
                      right: 3,
                    }}
                  />
                )}
                <Typography
                  fontSize="9px"
                  fontWeight={isCurrent ? 800 : isPast ? 600 : 500}
                  sx={{
                    color: isCurrent
                      ? s.color
                      : isPast
                        ? "#16a34a"
                        : "#94a3b8",
                  }}
                  noWrap
                >
                  {s.name}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Reassign Owner Modal ──────────────────────────────────────────────────────

function ReassignOwnerModal({
  open,
  currentOwnerId,
  orgId,
  lobId,
  lobName,
  onClose,
  onSelect,
}: {
  open: boolean;
  currentOwnerId: string;
  orgId: string | null;
  lobId: string | null;
  lobName?: string | null;
  onClose: () => void;
  onSelect: (user: AdminUser) => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users-for-assign", orgId ?? "all"],
    queryFn: () =>
      usersAdminApi.getAll({
        pageSize: 1000,
        status: "Active",
        ...(orgId ? { orgId } : {}),
      }),
    enabled: open,
    staleTime: 2 * 60_000,
  });

  // Eligibility rules (same as lead reassignment):
  //  • Must belong to the same Organization as the deal (already filtered server-side via orgId param).
  //  • Org Admins (no specific LOB assigned — lobId is null) are eligible for any LOB in their org.
  //  • Normal users must belong to the exact same LOB as the deal.
  //  • SUPER_ADMIN accounts are never assignable.
  const eligibleUsers: AdminUser[] = (data?.items ?? []).filter((u) => {
    if (u.roleName?.toUpperCase() === "SUPER_ADMIN") return false;
    if (orgId && u.orgId !== orgId) return false;
    if (!u.lobId) return true; // Org Admin — eligible across all LOBs in the org
    if (lobId && u.lobId !== lobId) return false;
    return true;
  });

  const users: AdminUser[] = eligibleUsers;
  const filtered = users.filter((u) =>
    `${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );
  const noEligibleUsers = !isLoading && !!data && eligibleUsers.length === 0;

  const handleConfirm = () => {
    const user = users.find((u) => u.id === selectedId);
    if (!user) return;
    onSelect(user);
    setSelectedId(null);
    setSearch("");
  };

  const handleClose = () => {
    setSelectedId(null);
    setSearch("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <PersonIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">
          Reassign Deal
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {/* Search box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            px: 1.25,
            py: 0.75,
            borderRadius: "8px",
            border: "1.5px solid #e5e7eb",
            bgcolor: "#fafafa",
            "&:focus-within": { borderColor: themeColors.primary },
          }}
        >
          <PersonIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
          <Box
            component="input"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            placeholder="Search by name or email…"
            autoFocus
            sx={{
              flex: 1,
              border: "none",
              outline: "none",
              bgcolor: "transparent",
              fontSize: "13px",
              color: "text.primary",
            }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <PersonIcon sx={{ fontSize: 36, color: "#e0e0e0", mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">
              {noEligibleUsers
                ? `No eligible users in ${lobName ?? "this LOB"}. Only users in the same LOB, or Organization Admins, can be assigned.`
                : "No users found"}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.75} sx={{ maxHeight: 280, overflowY: "auto" }}>
            {filtered.map((u) => {
              const isSelected = selectedId === u.id;
              const isCurrent = u.id === currentOwnerId;
              return (
                <Box
                  key={u.id}
                  onClick={() => !isCurrent && setSelectedId(u.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: "10px",
                    cursor: isCurrent ? "default" : "pointer",
                    border: `1.5px solid ${
                      isSelected
                        ? themeColors.primary
                        : isCurrent
                          ? "#bbf7d0"
                          : "#e5e7eb"
                    }`,
                    bgcolor: isSelected
                      ? `${themeColors.primary}08`
                      : isCurrent
                        ? "#f0fdf4"
                        : "#fafafa",
                    transition: "all 0.15s",
                    "&:hover": !isCurrent
                      ? {
                          borderColor: themeColors.primary,
                          bgcolor: `${themeColors.primary}06`,
                        }
                      : {},
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "12px",
                      fontWeight: 700,
                      bgcolor: isSelected
                        ? themeColors.primary
                        : isCurrent
                          ? "#16a34a"
                          : "#9ca3af",
                      flexShrink: 0,
                    }}
                  >
                    {u.name
                      .split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography fontSize="13px" fontWeight={600} noWrap>
                        {u.name}
                      </Typography>
                      {isCurrent && (
                        <Chip
                          label="Current"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "9.5px",
                            fontWeight: 700,
                            bgcolor: "#dcfce7",
                            color: "#16a34a",
                            "& .MuiChip-label": { px: "5px" },
                          }}
                        />
                      )}
                    </Box>
                    <Typography fontSize="11.5px" color="text.secondary" noWrap>
                      {u.email}
                    </Typography>
                    {u.roleName && (
                      <Typography fontSize="10.5px" color="text.disabled" noWrap>
                        {u.roleName}
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckIcon
                      sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }}
                    />
                  )}
                  {isCurrent && !isSelected && (
                    <CheckIcon
                      sx={{ fontSize: 16, color: "#16a34a", flexShrink: 0 }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedId}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            bgcolor: themeColors.primary,
          }}
        >
          Reassign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Assignment History Modal ──────────────────────────────────────────────────

function AssignmentHistoryModal({
  open,
  dealId,
  onClose,
}: {
  open: boolean;
  dealId: string;
  onClose: () => void;
}) {
  const { data: timeline = [], isLoading } = useQuery({
    queryKey: ["deal-timeline", dealId],
    queryFn: () => dealsApi.getTimeline(dealId),
    enabled: open,
  });

  const history = timeline.filter(
    (a) =>
      a.activityType === "OWNER_CHANGED" ||
      a.activityType === "REASSIGNED" ||
      a.activityType === "ASSIGNMENT"
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <SwapIcon sx={{ fontSize: 20, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">
          Assignment History
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, pb: 1, minHeight: 120 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : history.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
              gap: 1,
              color: "text.disabled",
            }}
          >
            <HistoryIcon sx={{ fontSize: 36 }} />
            <Typography fontSize="13px">No reassignment history yet</Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {history.map((entry, idx) => {
              let fromName = "";
              let toName = "";
              try {
                const meta = JSON.parse(entry.metadata ?? "{}");
                fromName = meta.fromName ?? "";
                toName = meta.toName ?? "";
              } catch {
                // fall back to description
              }
              const isLast = idx === history.length - 1;
              return (
                <Box
                  key={entry.id}
                  sx={{ display: "flex", gap: 1.5, position: "relative" }}
                >
                  {/* timeline line */}
                  {!isLast && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 15,
                        top: 32,
                        bottom: 0,
                        width: "2px",
                        bgcolor: "#e5e7eb",
                      }}
                    />
                  )}
                  {/* dot */}
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      bgcolor: `${themeColors.primary}15`,
                      border: `2px solid ${themeColors.primary}40`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.5,
                      zIndex: 1,
                    }}
                  >
                    <SwapIcon sx={{ fontSize: 14, color: themeColors.primary }} />
                  </Box>
                  {/* content */}
                  <Box sx={{ pb: 2.5, flex: 1 }}>
                    <Typography fontSize="13px" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                      {fromName && toName ? (
                        <>
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            {fromName}
                          </Box>
                          {" → "}
                          <Box component="span" sx={{ color: themeColors.primary }}>
                            {toName}
                          </Box>
                        </>
                      ) : (
                        entry.description
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                      {entry.createdBy && (
                        <Typography fontSize="11px" color="text.secondary">
                          by {entry.createdBy.name}
                        </Typography>
                      )}
                      <Typography fontSize="11px" color="text.disabled">
                        {new Date(entry.createdAt).toLocaleString(undefined, {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "8px" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit Deal Modal ───────────────────────────────────────────────────────────

export function EditDealModal({
  deal,
  stages,
  open,
  onClose,
}: {
  deal: Deal;
  stages: PipelineStage[];
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [form, setForm] = useState({
    title: deal.title,
    stage: deal.stage,
    amount: deal.amount != null ? String(deal.amount) : "",
    probabilityPct:
      deal.probabilityPct != null ? String(deal.probabilityPct) : "",
    priority: deal.priority,
    source: deal.source ?? "",
    description: deal.description ?? "",
    expectedCloseDate: deal.expectedCloseDate
      ? deal.expectedCloseDate.split("T")[0]
      : "",
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: () =>
      dealsApi.update(deal.id, {
        title: form.title.trim(),
        stage: form.stage,
        amount: form.amount ? parseFloat(form.amount) : null,
        currency: deal.currency || "INR",
        probabilityPct: form.probabilityPct
          ? parseInt(form.probabilityPct)
          : null,
        priority: form.priority,
        source: form.source || null,
        description: form.description || null,
        expectedCloseDate: form.expectedCloseDate || null,
        customerId: deal.customerId,
        contactId: deal.contactId,
        ownerId: deal.owner?.id || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", deal.id] });
      qc.invalidateQueries({ queryKey: ["deal-timeline", deal.id] });
      qc.invalidateQueries({ queryKey: ["deal-tasks", deal.id] });
      qc.invalidateQueries({ queryKey: ["deals-list"] });
      toast.success("Deal updated");
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to update deal"),
  });

  const activeStages = stages.filter((s) => !s.isWon && !s.isLost);

  return (
    <>
      <EntityModal
        open={open}
        onClose={onClose}
        onSubmit={() => setIsConfirmOpen(true)}
        title="Edit Deal"
        submitLabel="Save Changes"
        isSubmitting={mutation.isPending}
        submitDisabled={!form.title.trim()}
        maxWidth="sm"
        icon={<EditIcon sx={{ fontSize: 18 }} />}
      >
        <Stack spacing={2}>
          <FormInput
            label="Deal Name *"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            fullWidth
          />
          <FormSelect
            label="Stage"
            value={form.stage}
            onChange={(v: any) => {
              const val = v?.value ?? "";
              set("stage", val);
              const stg = stages.find((s) => s.name === val);
              if (stg?.probabilityPct != null)
                set("probabilityPct", String(stg.probabilityPct));
            }}
            options={stages
              .filter(s => {
                const currentIdx = stages.findIndex(x => x.name === deal.stage);
                const stageIdx = stages.findIndex(x => x.name === s.name);
                return stageIdx === currentIdx || stageIdx === currentIdx + 1;
              })
              .map(s => ({ label: s.name, value: s.name }))}
            fullWidth
          />
          {/* Auto-task hint */}
          {(() => {
            const stg = stages.find((s) => s.name === form.stage);
            return stg?.autoTaskTitle ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "8px",
                  bgcolor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                }}
              >
                <TaskIcon
                  sx={{ fontSize: 13, color: "#16a34a", flexShrink: 0 }}
                />
                <Typography fontSize="11.5px" color="#166534">
                  Moving to this stage will auto-create task:{" "}
                  <strong>"{stg.autoTaskTitle}"</strong>
                </Typography>
              </Box>
            ) : null;
          })()}
          <FormInput
            label="Amount (INR)"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            type="number"
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <FormInput
              label="Probability %"
              value={form.probabilityPct}
              onChange={(e) => set("probabilityPct", e.target.value)}
              type="number"
              sx={{ flex: 1 }}
              inputProps={{ min: 0, max: 100 }}
            />
            <FormSelect
              label="Priority"
              value={form.priority}
              onChange={(v: any) => set("priority", v?.value ?? "MEDIUM")}
              options={["LOW", "MEDIUM", "HIGH", "URGENT"].map((p) => ({
                label: DEAL_PRIORITY_LABELS[p as any] ?? p,
                value: p,
              }))}
              sx={{ flex: 1 }}
            />
          </Box>
          <FormSelect
            label="Source"
            value={form.source}
            onChange={(v: any) => set("source", v?.value ?? "")}
            options={
              [
                { label: "— None —", value: "" },
                ...Object.entries(DEAL_SOURCE_LABELS).map(([k, v]) => ({
                  label: v,
                  value: k,
                })),
              ] as any
            }
            fullWidth
          />
          <FormDateInput
            label="Expected Close Date"
            value={form.expectedCloseDate}
            onChange={(e) => set("expectedCloseDate", e.target.value)}
            fullWidth
            inputProps={{
              min: (() => {
                const now = new Date();
                const tzOffset = now.getTimezoneOffset() * 60000;
                return new Date(now.getTime() - tzOffset).toISOString().split("T")[0];
              })()
            }}
          />
          <FormInput
            label="Description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </EntityModal>

      <ConfirmDialog
        open={isConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this deal?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          mutation.mutate();
        }}
      />
    </>
  );
}

// ── Mark Lost Modal ───────────────────────────────────────────────────────────

export function MarkLostModal({
  deal,
  open,
  onClose,
  onSuccess,
}: {
  deal: Deal;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () => dealsApi.markLost(deal.id, reason.trim() || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", deal.id] });
      if (onSuccess) onSuccess();
      toast.success("Deal marked as lost");
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? "Failed"),
  });
  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={() => mutation.mutate()}
      title="Mark as Lost"
      submitLabel="Mark Lost"
      isSubmitting={mutation.isPending}
      maxWidth="xs"
      submitProps={{ color: "error" }}
      icon={<LostIcon sx={{ fontSize: 18 }} />}
    >
      <Stack spacing={2}>
        <Typography fontSize="13px" color="text.secondary">
          Mark <strong>{deal.title}</strong> as lost?
        </Typography>
        <FormInput
          label="Loss Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          placeholder="e.g. Budget constraints, chose competitor..."
        />
      </Stack>
    </EntityModal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const DealDetailPage = observer(function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tabParam = new URLSearchParams(window.location.search).get('tab');
  const initialTab = tabParam === 'tasks' ? 2 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [editOpen, setEditOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [isMoveToSalesConfirmOpen, setIsMoveToSalesConfirmOpen] = useState(false);
  const [emailTo, setEmailTo] = useState<string[]>([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [callPickerOpen, setCallPickerOpen] = useState(false);
  const [headerCreateEventOpen, setHeaderCreateEventOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [historyOpen,  setHistoryOpen]  = useState(false);



  const { hasAction } = usePermission()
  const canEdit   = hasAction('/deals', 'update')
  const canDelete = hasAction('/deals', 'delete')
  const canAssign = hasAction('/deals', 'assign')
  const canUpdateRequirements = hasAction('/deals', 'update_requirements') || hasAction('/deals', 'update') || hasAction('/deals', 'manage')

  // ── Privilege action keys ──────────────────────────────────────────────────
  const canCallLog         = hasAction('/deals', 'call_log')
  const canViewCalls       = hasAction('/deals', 'call_history')
  const canCallDownload    = hasAction('/deals', 'call_download')
  const canCallTranscript  = hasAction('/deals', 'call_transcript')
  const canComposeMail     = hasAction('/deals', 'compose_mail')
  const canViewMail        = hasAction('/deals', 'view_mail')
  const canAddComment      = hasAction('/deals', 'add_comment')
  const canViewComments    = hasAction('/deals', 'view_comments')
  const canAddTask         = hasAction('/deals', 'add_task')
  const canViewTasks       = hasAction('/deals', 'view_tasks')
  const canAddEvent        = hasAction('/deals', 'add_event')
  const canViewEvents      = hasAction('/deals', 'view_events')
  const canAddAttachment   = hasAction('/deals', 'add_attachment')
  const canViewAttachments = hasAction('/deals', 'view_attachments')
  const canManageStage     = hasAction('/deals', 'manage')
  const canUpdatePipeline  = hasAction('/deals', 'update_pipeline')
  const canConvert         = hasAction('/deals', 'convert')

  const { data: deal, isLoading } = useQuery({
    queryKey: ["deal", id],
    queryFn: () => dealsApi.getById(id!),
    enabled: !!id,
  });

  const { data: activities = [], isLoading: loadingActs } = useQuery({
    queryKey: ["deal-timeline", id],
    queryFn: () => dealsApi.getTimeline(id!),
    enabled: !!id,
  });

  const groupedCount = useMemo(() => {
    const safeLogs = normalizeActivities(activities);
    return groupActivities(safeLogs).length;
  }, [activities]);

  const { data: calls = [] } = useQuery({
    queryKey: ["deal-call-logs", id],
    queryFn: () => callLogsApi.getByDeal(id!),
    enabled: !!id,
  });

  const { data: emails = [] } = useQuery({
    queryKey: ["emails", "DEAL", id],
    queryFn: () => emailApi.getByEntity("DEAL", id!),
    enabled: !!id,
  });

  const { data: events = [] } = useQuery({
    queryKey: ["deal-follow-ups", id],
    queryFn: () => dealFollowUpsApi.getAll(id!),
    enabled: !!id,
  });

  // Fetch full customer to get orgName / lobName / warehouseName
  const { data: customerDetail } = useQuery({
    queryKey: ["customer-for-deal", deal?.customerId],
    queryFn: () => customersApi.getById(deal!.customerId!),
    enabled: !!deal?.customerId,
    staleTime: 5 * 60_000,
  });

  const { data: contactsForCallData } = useQuery({
    queryKey: ["contacts-for-call", deal?.customerId],
    queryFn: () => contactsApi.getAll({ customerId: deal!.customerId!, limit: 100 }),
    enabled: !!deal?.customerId,
  });

  // Fetch full lead to get address / gst / pincode if customer is null but leadId is present
  const { data: leadDetail } = useQuery({
    queryKey: ["lead-for-deal", deal?.leadId],
    queryFn: () => leadsApi.getById(deal!.leadId!),
    enabled: !!deal?.leadId && !deal?.customerId,
    staleTime: 5 * 60_000,
  });

  const { data: stages = [] } = useQuery({
    queryKey: ["pipeline-stages", deal?.orgId],
    queryFn: () => dealsApi.getStages(undefined, deal?.orgId || undefined),
    enabled: !!deal,
    staleTime: 5 * 60_000,
  });

  const { data: salesRecord } = useQuery({
    queryKey: ["sales-for-deal", id],
    queryFn: () => salesApi.getByDealId(id!),
    enabled: !!id && (deal?.status === "SALES" || deal?.status === "WON" || deal?.status === "LOST" || !!deal?.isWon || !!deal?.isLost),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const isAtLastActiveStage = useMemo(() => {
    if (!deal || !stages || stages.length === 0) return false;
    const activeStages = stages.filter((s) => !s.isWon && !s.isLost);
    const currentIdx = activeStages.findIndex((s) => s.name === deal.stage);
    return currentIdx !== -1 && currentIdx === activeStages.length - 1;
  }, [deal, stages]);

  const handleLogCall = () => {
    if (!deal?.customerId) {
      setCallPickerOpen(true);
      return;
    }
    const contacts = contactsForCallData?.items ?? [];
    const withPhone = contacts.filter((c) => c.phone);
    if (withPhone.length === 1) {
      callStore.showPlaceCall({
        entityType: 'DEAL',
        entityId: deal.id,
        name: withPhone[0].name,
        phone: withPhone[0].phone,
        companyName: deal.customer?.companyName ?? deal.customer?.name ?? undefined
      });
    } else {
      setCallPickerOpen(true);
    }
  };

  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ["deal-notes", id],
    queryFn: () => dealsApi.getNotes(id!),
    enabled: !!id && activeTab === 3,
  });

  const markWonMut = useMutation({
    mutationFn: () => dealsApi.changeStatus(id!, "SALES"),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      toast.success("Deal moved to Sales!");
      try {
        const salesRecord = await salesApi.getByDealId(id!);
        if (salesRecord?.id) {
          navigate(`/sales/${salesRecord.id}`);
        }
      } catch (e) {
        // ignore
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: () => dealsApi.delete(id!),
    onSuccess: () => {
      toast.success("Deal deleted");
      navigate("/deals");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to delete"),
  });

  const addNoteMut = useMutation({
    mutationFn: (content: string) => dealsApi.addNote(id!, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-notes", id] });
      toast.success("Note added");
    },
  });
  const updateNoteMut = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) =>
      dealsApi.updateNote(id!, noteId, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-notes", id] });
      toast.success("Note updated");
    },
  });
  const deleteNoteMut = useMutation({
    mutationFn: (noteId: string) => dealsApi.deleteNote(id!, noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-notes", id] });
      toast.success("Note deleted");
    },
  });

  const updateDescMut = useMutation({
    mutationFn: (description: string | null) => {
      return dealsApi.update(id!, {
        title: deal?.title ?? "",
        stage: deal?.stage ?? "",
        amount: deal?.amount ?? null,
        currency: deal?.currency ?? "INR",
        probabilityPct: deal?.probabilityPct ?? null,
        priority: deal?.priority ?? "MEDIUM",
        source: deal?.source ?? null,
        description,
        expectedCloseDate: deal?.expectedCloseDate ?? null,
        customerId: deal?.customerId ?? null,
        contactId: deal?.contactId ?? null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      qc.invalidateQueries({ queryKey: ["deal-timeline", id] });
      toast.success("Description updated");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to update"),
  });

  const updateReqsMut = useMutation({
    mutationFn: (requirements: string | null) => dealsApi.updateRequirements(id!, requirements),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      qc.invalidateQueries({ queryKey: ["deal-timeline", id] });
      toast.success("Requirements updated");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to update requirements"),
  });

  const changeStageMut = useMutation({
    mutationFn: (stage: string) =>
      dealsApi.changeStage(id!, deal!, stage, stages),
    onSuccess: (updated) => {
      qc.setQueryData(["deal", id], updated);
      qc.invalidateQueries({ queryKey: ["deal-timeline", id] });
      qc.invalidateQueries({ queryKey: ["deal-tasks", id] });
      toast.success(`Moved to "${updated.stage}"`);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to update stage"),
  });

  const reassignMut = useMutation({
    mutationFn: (ownerId: string) =>
      dealsApi.update(id!, {
        title: deal?.title ?? "",
        stage: deal?.stage ?? "",
        amount: deal?.amount ?? null,
        currency: deal?.currency ?? "INR",
        probabilityPct: deal?.probabilityPct ?? null,
        priority: deal?.priority ?? "MEDIUM",
        source: deal?.source ?? null,
        description: deal?.description ?? null,
        expectedCloseDate: deal?.expectedCloseDate ?? null,
        customerId: deal?.customerId ?? undefined,
        contactId: deal?.contactId ?? undefined,
        ownerId: deal?.owner.id,
        assignedToId: ownerId,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(["deal", id], updated);
      qc.invalidateQueries({ queryKey: ["deal-timeline", id] });
      const name = updated.assignedTo?.name || "Unassigned";
      toast.success(`Deal reassigned to ${name}`);
      setReassignOpen(false);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to reassign deal"),
  });

  const sendEmailMut = useMutation({
    mutationFn: (data: SendEmailData) =>
      emailApi.send({
        entityType: "DEAL",
        entityId: id,
        to: data.to,
        cc: data.cc.length > 0 ? data.cc : undefined,
        bcc: data.bcc.length > 0 ? data.bcc : undefined,
        subject: data.subject,
        htmlBody: data.body,
        files: data.attachments.length > 0 ? data.attachments : undefined,
      }),
    onSuccess: () => {
      toast.success("Email sent");
      setEmailOpen(false);
      qc.invalidateQueries({ queryKey: ["emails", "DEAL", id] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message ?? "Failed to send email"),
  });

  const updateAddressAndTaxMut = useMutation({
    mutationFn: async (payload: { address: string | null; gst: string | null; pincode: string | null }) => {
      const oldAddress = customerDetail?.address ?? leadDetail?.address ?? "";
      const oldPincode = customerDetail?.pincode ?? leadDetail?.pincode ?? "";
      const oldGst = customerDetail?.gst ?? leadDetail?.gst ?? "";

      const newAddress = payload.address ?? "";
      const newPincode = payload.pincode ?? "";
      const newGst = payload.gst ?? "";

      if (deal.customerId) {
        await customersApi.update(deal.customerId, payload);
      } else if (deal.leadId) {
        await leadsApi.update(deal.leadId, payload);
      }

      // Check and log Address Information Updated
      if (oldAddress !== newAddress) {
        await dealsApi.addActivity(deal.id, {
          type: "UPDATED",
          description: "Updated Address",
          metadataJson: JSON.stringify({ field: "Address", from: oldAddress || "—", to: newAddress || "—" }),
          createdById: authStore.user?.userId || ""
        });
      }
      if (oldPincode !== newPincode) {
        await dealsApi.addActivity(deal.id, {
          type: "UPDATED",
          description: "Updated Pincode",
          metadataJson: JSON.stringify({ field: "Pincode", from: oldPincode || "—", to: newPincode || "—" }),
          createdById: authStore.user?.userId || ""
        });
      }

      // Check and log Tax Information Updated
      if (oldGst !== newGst) {
        await dealsApi.addActivity(deal.id, {
          type: "UPDATED",
          description: "Updated Tax Information",
          metadataJson: JSON.stringify({ field: "GST Number", from: oldGst || "—", to: newGst || "—" }),
          createdById: authStore.user?.userId || ""
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-timeline", id] });
      qc.invalidateQueries({ queryKey: ["customer-for-deal", deal?.customerId] });
      qc.invalidateQueries({ queryKey: ["lead-for-deal", deal?.leadId] });
      toast.success("Address & Tax Info updated");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to update Address & Tax Info");
    }
  });

  // Calling mutations removed (using global callStore)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!deal) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Deal not found</Typography>
        <Button onClick={() => navigate("/deals")} sx={{ mt: 2 }}>
          Back to Deals
        </Button>
      </Box>
    );
  }

  const statusColors = STATUS_COLORS[deal.status] ?? STATUS_COLORS.OPEN;
  const isWon = deal.status === "WON" || deal.isWon;
  const isLost = deal.status === "LOST" || deal.isLost;
  const isSales = deal.status === "SALES";
  const priorityColors =
    PRIORITY_COLORS[deal.priority] ?? PRIORITY_COLORS.MEDIUM;

  // Deal is closed (won, sales or lost) — disable all activity creation
  const isClosed = isWon || isLost || isSales;

  const TABS = [
    `Activity${groupedCount > 0 ? ` (${groupedCount})` : ""}`,
    `Call${calls.length > 0 ? ` (${calls.length})` : ""}`,
    `Email${emails.length > 0 ? ` (${emails.length})` : ""}`,
    `Comments${deal.noteCount > 0 ? ` (${deal.noteCount})` : ""}`,
    `Tasks${deal.taskCount > 0 ? ` (${deal.taskCount})` : ""}`,
    `Events${events.length > 0 ? ` (${events.length})` : ""}`,
    "Attachments",
  ];

  const dealTabs = [
    {
      label: TABS[0],
      content: (
        <UniversalActivityTimeline
          logs={activities}
          isLoading={loadingActs}
          entityType="sales"
        />
      ),
    },
    ...(canViewCalls ? [{
      label: TABS[1],
      content: (
        <DealCallsSection
          dealId={deal.id}
          onLogCall={handleLogCall}
          canModify={!isClosed && canCallLog}
        />
      ),
    }] : []),
    ...(canViewMail ? [{
      label: TABS[2],
      content: (
        <EmailHistorySection
          entityType="DEAL"
          entityId={deal.id}
          canModify={!isClosed && canComposeMail}
          onCompose={() => setContactPickerOpen(true)}
        />
      ),
    }] : []),
    ...(canViewComments ? [{
      label: TABS[3],
      content: (
        <Box>
          {!isClosed && canAddComment && (
            <CommentInput
              onAdd={async (c) => {
                await addNoteMut.mutateAsync(c);
              }}
              isAdding={addNoteMut.isPending}
            />
          )}
          {loadingNotes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notes.length === 0 ? (
            <CommentsEmptyState />
          ) : (
            <Stack spacing={0}>
              {notes.map((note, index) => (
                <CommentItem
                  key={note.id}
                  note={note}
                  onUpdate={!isClosed && canAddComment ? async (c) => {
                    await updateNoteMut.mutateAsync({
                      noteId: note.id,
                      content: c,
                    });
                  } : undefined}
                  onDelete={!isClosed && canAddComment ? () => deleteNoteMut.mutate(note.id) : undefined}
                  isUpdating={updateNoteMut.isPending}
                  isLast={index === notes.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      ),
    }] : []),
    ...(canViewTasks ? [{
      label: TABS[4],
      content: <DealTasksSection dealId={deal.id} canModify={!isClosed && canAddTask} />,
    }] : []),
    ...(canViewEvents ? [{
      label: TABS[5],
      content: (
        <DealEventsSection
          dealId={deal.id}
          canModify={!isClosed && canAddEvent}
          externalCreateOpen={headerCreateEventOpen}
          onExternalCreateClose={() => setHeaderCreateEventOpen(false)}
        />
      ),
    }] : []),
    ...(canViewAttachments ? [{
      label: TABS[6],
      content: (
        <AttachmentsSection
          moduleType="deal"
          recordId={deal.id}
          canModify={!isClosed && canAddAttachment}
        />
      ),
    }] : []),
  ];

  const isSalesStage = deal.status === "SALES" || deal.stage === "Sales";
  const stg = isSalesStage ? null : stages.find((s) => s.name === deal.stage);
  const stageColor = isSalesStage ? "#7c3aed" : (stg?.color || "#6d28d9");

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        bgcolor: "#f5f6fa",
        "&::-webkit-scrollbar": { width: "5px" },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "#d1d5db",
          borderRadius: "4px",
        },
      }}
    >
      {/* ── Breadcrumb ── */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate("/deals")}
          sx={{ color: "#6b7280", mr: 0.5 }}
        >
          <BackIcon fontSize="small" />
        </IconButton>
        <Typography
          fontSize="13px"
          color="text.secondary"
          sx={{ cursor: "pointer", "&:hover": { color: "text.primary" } }}
          onClick={() => navigate("/deals")}
        >
          Deals
        </Typography>
        <Typography fontSize="13px" color="text.secondary" sx={{ mx: 0.25 }}>
          /
        </Typography>
        <Typography fontSize="13px" fontWeight={600} color="text.primary">
          {deal.title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {!isClosed && canEdit && (
            <Tooltip title="Edit deal">
              <IconButton
                size="small"
                onClick={() => setEditOpen(true)}
                sx={{ color: "#6b7280" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete deal">
              <IconButton
                size="small"
                onClick={() => setDeleteOpen(true)}
                sx={{ color: "#ef4444" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Hero Card ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
        <Paper
          elevation={0}
          sx={{
            border: `2px solid ${themeColors.primary}45`,
            borderRadius: "14px",
            p: 2.5,
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* Avatar */}
            <Avatar
              sx={{
                width: 62,
                height: 62,
                bgcolor: "#ede9fe",
                color: "#6d28d9",
                flexShrink: 0,
                boxShadow: "0 4px 12px #6d28d930",
              }}
            >
              <DealIcon sx={{ fontSize: 30 }} />
            </Avatar>

            {/* Title + chips */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  fontWeight={700}
                  fontSize="18px"
                  color="text.primary"
                >
                  {deal.title}
                </Typography>
                <Chip
                  label={DEAL_STATUS_LABELS[deal.status]}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: "10px",
                    fontSize: "10.5px",
                    fontWeight: 700,
                    bgcolor: statusColors.bg,
                    color: statusColors.color,
                    "& .MuiChip-label": { px: "7px" },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={isSalesStage ? "Sales" : deal.stage}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: "4px",
                    fontSize: "10.5px",
                    fontWeight: 700,
                    bgcolor: `${stageColor}22`,
                    color: stageColor,
                    border: `1px solid ${stageColor}44`,
                    "& .MuiChip-label": { px: "7px" },
                  }}
                />
                <Typography fontSize="12.5px" color="text.secondary">
                  Expected Order Value: {fmtAmount(deal.amount, deal.currency)}
                </Typography>
                <Typography fontSize="12.5px" color="text.secondary">
                  Expected Closing Date: {fmt(deal.expectedCloseDate)}
                </Typography>
                {deal.pipeline && (
                  <Typography fontSize="12px" color="text.disabled">
                    {deal.pipeline.name}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              {/* Deal origin — the lead this deal was created from */}
              {deal.leadId && (
                <Tooltip title="Open the lead this deal was created from">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonIcon sx={{ fontSize: "14px !important" }} />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
                    onClick={() => navigate(`/leads/${deal.leadId}`)}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: themeColors.primary,
                      borderColor: `${themeColors.primary}66`,
                      bgcolor: `${themeColors.primary}08`,
                      "&:hover": {
                        borderColor: themeColors.primary,
                        bgcolor: `${themeColors.primary}14`,
                      },
                    }}
                  >
                    Deal Origin{deal.lead?.name ? `: ${deal.lead.name}` : ""}
                  </Button>
                </Tooltip>
              )}
              {!isClosed && (
                <>
                  {canCallLog && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PhoneIcon sx={{ fontSize: "14px !important" }} />}
                      disabled={callStore.isStarting}
                      onClick={handleLogCall}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#374151",
                        borderColor: "#d1d5db",
                        "&:hover": {
                          borderColor: themeColors.primary,
                          color: themeColors.primary,
                        },
                      }}
                    >
                      {callStore.isStarting ? (
                        <CircularProgress size={13} sx={{ mr: 0.5 }} />
                      ) : null}
                      Log Call
                    </Button>
                  )}
                  {canAddEvent && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        <CalendarIcon sx={{ fontSize: "14px !important" }} />
                      }
                      onClick={() => {
                        setActiveTab(5);
                        setHeaderCreateEventOpen(true);
                      }}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#374151",
                        borderColor: "#d1d5db",
                        "&:hover": {
                          borderColor: themeColors.primary,
                          color: themeColors.primary,
                        },
                      }}
                    >
                      Schedule Meeting
                    </Button>
                  )}
                  {canComposeMail && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailIcon sx={{ fontSize: "14px !important" }} />}
                      onClick={() => setContactPickerOpen(true)}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#374151",
                        borderColor: "#d1d5db",
                        "&:hover": {
                          borderColor: themeColors.primary,
                          color: themeColors.primary,
                        },
                      }}
                    >
                      Send Email
                    </Button>
                  )}
                </>
              )}
              {deal.status === "OPEN" && canManageStage && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    <LostIcon sx={{ fontSize: "14px !important" }} />
                  }
                  onClick={() => setLostOpen(true)}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "#dc2626",
                    borderColor: "#dc2626",
                    "&:hover": { bgcolor: "#fef2f2", borderColor: "#dc2626" },
                  }}
                >
                  Lost
                </Button>
              )}
              {deal.customer && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/customers/${deal.customerId}`)}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "#374151",
                    borderColor: "#d1d5db",
                    "&:hover": {
                      borderColor: themeColors.primary,
                      color: themeColors.primary,
                    },
                  }}
                >
                  View Company
                </Button>
              )}
              {deal.status === "OPEN" && canManageStage && (
                <Tooltip title={!isAtLastActiveStage ? "Deals can only be moved to Sales once all pipeline stages are completed" : ""}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: "14px !important" }} />}
                      onClick={() => setIsMoveToSalesConfirmOpen(true)}
                      disabled={markWonMut.isPending || !isAtLastActiveStage}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        background: themeColors.primary,
                        "&:hover": { background: themeColors.primaryDark },
                      }}
                    >
                      {markWonMut.isPending ? (
                        <CircularProgress size={13} sx={{ mr: 0.5 }} />
                      ) : null}
                      Move to Sales
                    </Button>
                  </span>
                </Tooltip>
              )}
              {(isSales || isWon || isLost || !!salesRecord) && (
                <Button
                  size="small"
                  variant="contained"
                  endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={async () => {
                    try {
                      const record = salesRecord ?? (await salesApi.getByDealId(deal.id));
                      if (record?.id) {
                        navigate(`/sales/${record.id}`);
                      } else {
                        toast.error("Sales record not found");
                      }
                    } catch (e) {
                      toast.error("Failed to load sale page");
                    }
                  }}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    background: themeColors.primary,
                    "&:hover": { background: themeColors.primaryDark },
                  }}
                >
                  View Sale
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Two-column body ── */}
      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          p: 3,
          pt: 2,
          alignItems: "flex-start",
        }}
      >
        {/* ── Left Sidebar ── */}
        <Box
          sx={{
            width: 260,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Stage Progress */}
          <SideCard title="Pipeline Stage">
            <StageProgressBar
              deal={deal}
              stages={stages}
              onStageClick={(stage) => changeStageMut.mutate(stage)}
              isChanging={changeStageMut.isPending}
            />
          </SideCard>

          {/* Deal Info */}
          <SideCard title="Deal Info">
            <Stack spacing={0.5}>
              <InfoRow
                icon={<AmountIcon sx={{ fontSize: 15 }} />}
                label="Value"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={700}
                    color={themeColors.primary}
                  >
                    {fmtAmount(deal.amount, deal.currency)}
                  </Typography>
                }
              />
              <InfoRow
                icon={<CalIcon sx={{ fontSize: 15 }} />}
                label="Close Date"
                value={fmt(deal.expectedCloseDate)}
              />
              {deal.probabilityPct != null && (
                <InfoRow
                  icon={<InfoIcon sx={{ fontSize: 15 }} />}
                  label="Probability"
                  value={`${deal.probabilityPct}%`}
                />
              )}
              <InfoRow
                icon={<InfoIcon sx={{ fontSize: 15 }} />}
                label="Priority"
                value={
                  <Chip
                    label={DEAL_PRIORITY_LABELS[deal.priority] ?? deal.priority}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "10.5px",
                      fontWeight: 700,
                      bgcolor: priorityColors.bg,
                      color: priorityColors.color,
                      borderRadius: "4px",
                      "& .MuiChip-label": { px: "6px" },
                    }}
                  />
                }
              />
              <InfoRow
                icon={<IndustryIcon sx={{ fontSize: 15 }} />}
                label="Customer Type"
                value={deal.customerTypeName ?? '—'}
              />
              {deal.source && (
                <InfoRow
                  icon={<InfoIcon sx={{ fontSize: 15 }} />}
                  label="Source"
                  value={DEAL_SOURCE_LABELS[deal.source as any] ?? deal.source}
                />
              )}
              {deal.closedAt && (
                <InfoRow
                  icon={<CalIcon sx={{ fontSize: 15 }} />}
                  label="Closed"
                  value={fmt(deal.closedAt)}
                />
              )}
              {deal.isLost && deal.lostReason && (
                <InfoRow
                  icon={<LostIcon sx={{ fontSize: 15 }} />}
                  label="Lost Reason"
                  value={
                    <Typography fontSize="12.5px" color="error.main">
                      {deal.lostReason}
                    </Typography>
                  }
                />
              )}
              <InfoRow
                icon={<CalIcon sx={{ fontSize: 15 }} />}
                label="Created"
                value={fmt(deal.createdAt)}
              />
              {deal.createdBy && (
                <InfoRow
                  icon={<PersonIcon sx={{ fontSize: 15 }} />}
                  label="Owner"
                  value={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Avatar
                        sx={{
                          width: 18,
                          height: 18,
                          fontSize: "9px",
                          fontWeight: 700,
                          bgcolor: avatarColor(deal.createdBy.name),
                        }}
                      >
                        {initials(deal.createdBy.name)}
                      </Avatar>
                      <Typography fontSize="12.5px" fontWeight={600} noWrap>
                        {deal.createdBy.name}
                      </Typography>
                      {deal.createdBy.roleName && (
                        <Chip
                          label={deal.createdBy.roleName}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "9px",
                            fontWeight: 600,
                            bgcolor: `${themeColors.primary}12`,
                            color: themeColors.primary,
                            borderRadius: "4px",
                            "& .MuiChip-label": { px: "5px" },
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              )}
            </Stack>
          </SideCard>

          <DescriptionSection
            value={deal.description}
            onSave={(val) => updateDescMut.mutate(val)}
            isPending={updateDescMut.isPending}
            canEdit={!isClosed}
            emptyText={isClosed ? "No description" : "No description — click ✎ to add one"}
          />

          {/* Requirements */}
          <RequirementsSideCard
            value={deal.requirements}
            onSave={(reqs) => updateReqsMut.mutateAsync(reqs)}
            isPending={updateReqsMut.isPending}
            canEdit={!isClosed && canUpdateRequirements}
          />

          {/* Customer & Contact */}
          <SideCard
            title={deal.customer ? "Company" : deal.lead ? "Lead" : "Customer"}
          >
            {deal.customer ? (
              <Box
                onClick={() => navigate(`/customers/${deal.customerId}`)}
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  border: `1px solid ${themeColors.border}`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: themeColors.primary,
                    bgcolor: "#f5f7fb",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    fontSize="13px"
                    fontWeight={700}
                    color={themeColors.primary}
                    sx={{ "&:hover": { textDecoration: "underline" } }}
                  >
                    {deal.customer.companyName || deal.customer.name}
                  </Typography>
                  <OpenInNewIcon
                    sx={{
                      fontSize: 13,
                      color: themeColors.primary,
                      flexShrink: 0,
                    }}
                  />
                </Box>
                {deal.customer.phone && (
                  <Typography
                    fontSize="11px"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.25,
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 11 }} /> {deal.customer.phone}
                  </Typography>
                )}
              </Box>
            ) : deal.lead ? (
              <Box
                onClick={() => navigate(`/leads/${deal.leadId}`)}
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  border: `1px solid ${themeColors.border}`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: themeColors.primary,
                    bgcolor: "#f5f7fb",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    fontSize="13px"
                    fontWeight={700}
                    color={themeColors.primary}
                    sx={{ "&:hover": { textDecoration: "underline" } }}
                  >
                    {deal.lead.name}
                  </Typography>
                  <OpenInNewIcon
                    sx={{ fontSize: 13, color: themeColors.primary, flexShrink: 0 }}
                  />
                </Box>
                {deal.lead.phone && (
                  <Typography
                    fontSize="11px"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
                  >
                    <PhoneIcon sx={{ fontSize: 11 }} /> {deal.lead.phone}
                  </Typography>
                )}
                {deal.lead.customerTypeName && (
                  <Typography
                    fontSize="11px"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
                  >
                    <IndustryIcon sx={{ fontSize: 11 }} /> {deal.lead.customerTypeName}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography fontSize="12px" color="text.secondary">
                No customer linked
              </Typography>
            )}
            {deal.contact && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
                <Typography
                  fontSize="11px"
                  color="text.secondary"
                  mb={0.75}
                  fontWeight={500}
                >
                  Primary Contact
                </Typography>
                <Box
                  onClick={() => navigate(`/contacts/${deal.contactId}`)}
                  sx={{
                    p: 1,
                    borderRadius: "10px",
                    border: `1px solid ${themeColors.border}`,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: themeColors.primary,
                      bgcolor: "#f5f7fb",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonIcon
                        sx={{ fontSize: 14, color: themeColors.primary }}
                      />
                      <Typography
                        fontSize="12.5px"
                        fontWeight={700}
                        color={themeColors.primary}
                      >
                        {deal.contact.name}
                      </Typography>
                    </Box>
                    <OpenInNewIcon
                      sx={{
                        fontSize: 13,
                        color: themeColors.primary,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                  {deal.contact.phone && (
                    <Typography
                      fontSize="11px"
                      color="text.secondary"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      <PhoneIcon sx={{ fontSize: 11 }} /> {deal.contact.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </SideCard>

          {/* Owner / Assignee */}
          <SideCard
            title="Assigned To"
            action={
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Assignment history">
                  <IconButton
                    size="small"
                    onClick={() => setHistoryOpen(true)}
                    sx={{
                      color: "#9ca3af",
                      "&:hover": { color: themeColors.primary, bgcolor: "#f5f7fb" },
                    }}
                  >
                    <HistoryIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {!isClosed && canAssign && (
                  <Tooltip title="Reassign deal">
                    <IconButton
                      size="small"
                      onClick={() => setReassignOpen(true)}
                      disabled={reassignMut.isPending}
                      sx={{
                        color: "#9ca3af",
                        "&:hover": { color: themeColors.primary, bgcolor: "#f5f7fb" },
                      }}
                    >
                      {reassignMut.isPending ? (
                        <CircularProgress size={13} />
                      ) : (
                        <EditIcon sx={{ fontSize: 14 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            }
          >
            <Box
              sx={{
                p: 1,
                borderRadius: "10px",
                border: `1px solid ${themeColors.border}`,
                bgcolor: "#fafafa",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: themeColors.primary,
                  bgcolor: "#f5f7fb",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "11px",
                      fontWeight: 700,
                      bgcolor: avatarColor(deal.assignedTo?.name || "Unassigned"),
                      flexShrink: 0,
                    }}
                  >
                    {initials(deal.assignedTo?.name || "Unassigned")}
                  </Avatar>
                  <Typography
                    fontSize="13px"
                    fontWeight={700}
                    color={themeColors.primary}
                    noWrap
                    sx={{ "&:hover": { textDecoration: "underline" } }}
                  >
                    {deal.assignedTo?.name || "Unassigned"}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 13, color: themeColors.primary, flexShrink: 0 }} />
              </Box>
              <Typography
                fontSize="11px"
                color="text.secondary"
                sx={{ mt: 0.25, ml: "36px" }}
                noWrap
              >
                {deal.assignedTo?.email || ""}
              </Typography>
              {deal.assignedTo?.roleName && (
                <Box sx={{ mt: 0.5, ml: "36px" }}>
                  <Chip
                    label={deal.assignedTo.roleName}
                    size="small"
                    sx={{
                      height: 17,
                      fontSize: "9.5px",
                      fontWeight: 600,
                      bgcolor: `${themeColors.primary}12`,
                      color: themeColors.primary,
                      borderRadius: "4px",
                      "& .MuiChip-label": { px: "6px" },
                    }}
                  />
                </Box>
              )}
            </Box>
            {!isClosed && canAssign && (
              <Box
                onClick={() => setReassignOpen(true)}
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  py: 0.6,
                  borderRadius: "8px",
                  border: `1.5px dashed ${themeColors.primary}55`,
                  cursor: "pointer",
                  color: themeColors.primary,
                  transition: "all 0.15s",
                  "&:hover": {
                    bgcolor: "#f5f7fb",
                    borderColor: themeColors.primary,
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 12 }} />
                <Typography fontSize="11px" fontWeight={600}>
                  Reassign Deal
                </Typography>
              </Box>
            )}

            <HelpAssigneeSelector
              currentPath="/deals"
              lobId={deal.lobId}
              currentHelpAssigneeId={deal.helpAssignee?.id}
              currentHelpAssigneeName={deal.helpAssignee?.name}
              disabled={isClosed}
              onUpdate={async (newId) => {
                await dealsApi.updateHelpAssignee(deal.id, newId)
                qc.invalidateQueries({ queryKey: ['deal', id] })
                qc.invalidateQueries({ queryKey: ['deal-timeline', id] })
              }}
            />
          </SideCard>

          {/* Business / LOB / Warehouse */}
          <SideCard title="Business Details">
            <Stack spacing={0.5}>
              <InfoRow
                icon={<OrgIcon sx={{ fontSize: 15 }} />}
                label="Business"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    color={
                      deal.orgName || customerDetail?.orgName
                        ? "text.primary"
                        : "text.disabled"
                    }
                  >
                    {deal.orgName || customerDetail?.orgName || "—"}
                  </Typography>
                }
              />
              <InfoRow
                icon={<LobIcon sx={{ fontSize: 15 }} />}
                label="LOB"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    color={
                      deal.lobName || customerDetail?.lobName
                        ? "text.primary"
                        : "text.disabled"
                    }
                  >
                    {deal.lobName || customerDetail?.lobName || "—"}
                  </Typography>
                }
              />
            </Stack>
          </SideCard>

          {/* Address & Tax Info */}
          <AddressAndTaxInfoCard
            address={customerDetail?.address ?? leadDetail?.address ?? null}
            gst={customerDetail?.gst ?? leadDetail?.gst ?? null}
            pincode={customerDetail?.pincode ?? leadDetail?.pincode ?? null}
            canEdit={!isClosed && canEdit && !!(deal.customerId || deal.leadId)}
            isSaving={updateAddressAndTaxMut.isPending}
            onSave={(data) => updateAddressAndTaxMut.mutate(data)}
          />

          {/* Counts quick nav */}
          <SideCard>
            {[
              {
                icon: <TimelineIcon sx={{ fontSize: 16 }} />,
                label: "Activity",
                count: activities.length,
                tab: 0,
              },
              {
                icon: <PhoneIcon sx={{ fontSize: 16 }} />,
                label: "Call",
                count: calls.length,
                tab: 1,
              },
              {
                icon: <NoteIcon sx={{ fontSize: 16 }} />,
                label: "Comments",
                count: deal.noteCount,
                tab: 3,
              },
              {
                icon: <TaskIcon sx={{ fontSize: 16 }} />,
                label: "Tasks",
                count: deal.taskCount,
                tab: 4,
              },
              {
                icon: <CalendarIcon sx={{ fontSize: 16 }} />,
                label: "Events",
                count: events.length,
                tab: 5,
              },
            ].map((item) => (
              <Box
                key={item.label}
                onClick={() => setActiveTab(item.tab)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f5f7fb" },
                }}
              >
                <Box sx={{ color: themeColors.primary }}>{item.icon}</Box>
                <Typography fontSize="12.5px" fontWeight={600} sx={{ flex: 1 }}>
                  {item.label}
                </Typography>
                {item.count != null && (
                  <Chip
                    label={item.count}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "10px",
                      fontWeight: 700,
                      bgcolor: "#f1f5f9",
                      color: "#64748b",
                      "& .MuiChip-label": { px: "6px" },
                    }}
                  />
                )}
              </Box>
            ))}
          </SideCard>
        </Box>

        {/* ── Right Panel ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EntityDetailTabs
            tabs={dealTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            padding={2.5}
            tabHeight={44}
            tabFontSize="13px"
            tabFontWeight={600}
            tabPaddingX={2}
            borderRadius="12px"
          />
        </Box>
      </Box>

      {/* ── Modals ── */}
      {reassignOpen && (
        <ReassignOwnerModal
          open={reassignOpen}
          currentOwnerId={deal.assignedTo?.id || ""}
          orgId={deal.orgId}
          lobId={deal.lobId}
          lobName={deal.lobName}
          onClose={() => setReassignOpen(false)}
          onSelect={(user) => reassignMut.mutate(user.id)}
        />
      )}
      {historyOpen && (
        <AssignmentHistoryModal
          open={historyOpen}
          dealId={deal.id}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {editOpen && (
        <EditDealModal
          deal={deal}
          stages={stages}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
      {lostOpen && (
        <MarkLostModal
          deal={deal}
          open={lostOpen}
          onClose={() => setLostOpen(false)}
        />
      )}

      {/* Global calling UI overlay (rendered in AppLayout) */}
      <SelectCallContactModal
        open={callPickerOpen}
        customerId={deal.customerId}
        onClose={() => setCallPickerOpen(false)}
        onSelect={(contact) => {
          setCallPickerOpen(false);
          callStore.showPlaceCall({
            entityType: 'DEAL',
            entityId: deal.id,
            name: contact.name,
            phone: contact.phone,
            companyName: deal.customer?.companyName ?? deal.customer?.name ?? undefined
          });
        }}
      />

      <SelectEmailContactModal
        open={contactPickerOpen}
        customerId={deal.customerId}
        onClose={() => setContactPickerOpen(false)}
        onSelect={(emails) => {
          setEmailTo(emails);
          setContactPickerOpen(false);
          setEmailOpen(true);
        }}
      />

      <SendEmailDrawer
        open={emailOpen}
        onClose={() => {
          setEmailOpen(false);
          setEmailTo([]);
        }}
        defaultTo={emailTo}
        onSend={(data: SendEmailData) => sendEmailMut.mutate(data)}
        isSending={sendEmailMut.isPending}
        orgId={deal?.orgId}
      />

      {/* Delete confirm */}
      <DeleteDialog
        open={deleteOpen}
        name={deal.title}
        title="Delete Deal"
        description={`This will permanently delete ${deal.title} and all associated data. This cannot be undone.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMut.mutate()}
        isDeleting={deleteMut.isPending}
      />

      <ConfirmDialog
        open={isMoveToSalesConfirmOpen}
        title="Move to Sales"
        description={`Are you sure you want to move ${deal.title} to Sales?`}
        confirmLabel="Move to Sales"
        confirmColor="primary"
        isConfirming={markWonMut.isPending}
        onClose={() => setIsMoveToSalesConfirmOpen(false)}
        onConfirm={() => {
          markWonMut.mutate(undefined, {
            onSuccess: () => {
              setIsMoveToSalesConfirmOpen(false)
            }
          })
        }}
      />
    </Box>
  );
});

export default DealDetailPage;
